import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Spinner,
  Badge,
  ProgressBar,
  Nav,
  Row,
  Col,
} from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { can } from "../../utils/access";
import AddEditRoundModal from "./AddEditRoundModal";
import RoundTimeline from "./RoundTimeline";
import { AppButton } from "../ui";
import { ChevronLeft, Plus, Pencil } from "lucide-react";
import "./InterviewDetailsPage.scss";

const statusColor = {
  Ongoing: "warning",
  Completed: "success",
  Offered: "success",
  Joined: "success",
  Rejected: "danger",
  Withdrawn: "danger",
  Cancelled: "danger",
  Scheduled: "primary",
};

const InterviewDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("timeline");
  const [showRoundModal, setShowRoundModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
  const [editingRound, setEditingRound] = useState(null); // round data or null
  const [savingRound, setSavingRound] = useState(false);

  // Role detection
  const user = getCurrentUser();
  const isStudent = user?.usertype === 'student';
  const isStudentRoute = location.pathname.startsWith('/s/');
  const [canStudentSchedule, setCanStudentSchedule] = useState(false);

  // Check if student self-scheduling is enabled
  useEffect(() => {
    if (!isStudent) return;
    apiClient.get('/institute/config')
      .then(res => setCanStudentSchedule(!!res.data?.config?.studentSelfScheduling))
      .catch(() => setCanStudentSchedule(false));
  }, [isStudent]);

  const canAddRound = isStudent ? canStudentSchedule : can("interviews.manage");
  const canEdit = isStudent ? canStudentSchedule : can("interviews.manage");
  const backPath = isStudentRoute ? '/s/interviews' : '/interviews';

  // Fetch interview on mount or after add/edit round
  const fetchInterview = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/interviews/${id}`);
      setInterview(data.interview);
    } catch {
      setInterview(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInterview();
    // eslint-disable-next-line
  }, [id]);

  // Compute progress
  const roundsCompleted =
    interview?.rounds?.filter(
      (r) =>
        r.status !== "Scheduled" &&
        r.status !== "Ongoing" &&
        r.status !== "Rescheduled"
    ).length || 0;
  const totalRounds = interview?.rounds?.length || 0;
  const percent = totalRounds
    ? Math.round((roundsCompleted / totalRounds) * 100)
    : 0;

  const handleAddRound = () => {
    setModalMode("add");
    setEditingRound(null);
    setShowRoundModal(true);
  };

  const handleEditRound = (round) => {
    setModalMode("edit");
    setEditingRound(round);
    setShowRoundModal(true);
  };

  // Save (add or edit) round – re-throws errors so AddEditRoundModal can
  // display conflict / validation feedback to the user.
  const handleSaveRound = async (roundData) => {
    setSavingRound(true);
    try {
      if (modalMode === "add") {
        await apiClient.post(`/interviews/${id}/add-round`, roundData);
      } else if (modalMode === "edit" && editingRound?._id) {
        await apiClient.put(
          `/interviews/${id}/edit-round/${editingRound._id}`,
          roundData
        );
      }
      setShowRoundModal(false);
      await fetchInterview();
    } catch (err) {
      throw err; // Let AddEditRoundModal handle 409 conflicts / other errors
    } finally {
      setSavingRound(false);
    }
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  if (!interview)
    return (
      <div className="text-center my-5 text-muted">Interview not found.</div>
    );

  return (
    <div className="interview-details-page">
      {/* Header bar */}
      <Row className="align-items-center justify-content-between mb-3 gx-2 gy-2 flex-wrap">
        <Col
          xs={12}
          md="auto"
          className="d-flex align-items-center gap-2 flex-wrap"
        >
          <Button
            variant="light"
            onClick={() => navigate(backPath)}
            className="p-2"
          >
            <ChevronLeft size="1em" />
          </Button>
          <span className="fw-semibold fs-5">Interview Details</span>
        </Col>
        <Col
          xs={12}
          md="auto"
          className="d-flex gap-2 justify-content-start justify-content-md-end"
        >
          {canAddRound && (
            <AppButton
              type="button"
              className="d-flex align-items-center"
              onClick={handleAddRound}
            >
              <Plus size="1em" className="me-2" /> Add Round
            </AppButton>
          )}
          {canEdit && (
            <Button
              variant="outline-secondary"
              onClick={() => navigate(`${backPath}/edit/${id}`)}
              className="d-flex align-items-center"
            >
              <Pencil size="1em" className="me-2" /> Edit
            </Button>
          )}
        </Col>
      </Row>

      {/* Info Card */}
      <Card className="mb-4 py-3 px-2 px-md-4 info-card">
        <Row className="align-items-center g-3 flex-md-nowrap">
          {/* Avatar */}
          <Col
            xs={3} 
            md="auto"
            className="text-center text-md-start flex-shrink-0"
          >
            <img
              src={
                interview.studentId?.avatar ||
                `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
                  interview.studentId?.name || "User"
                )}`
              }
              alt=""
              className="d-block mx-auto mx-md-0"
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                objectFit: "cover",
                background: "#f8fafb",
                border: "1px solid #eee",
              }}
            />
          </Col>
          {/* Student Info */}
          <Col xs={8} md className="mb-2 mb-md-0">
            <div className="fw-semibold fs-5">{interview.studentId?.name}</div>
            <div className="text-muted mt-1 mb-1" style={{ fontSize: 15 }}>
              +91 {interview.studentId?.mobile}
              {interview.studentId?.passoutYear &&
                ` • ${interview.studentId.passoutYear}`}
            </div>
            <div className="text-muted" style={{ fontSize: 15 }}>
              {interview.studentId?.email}
            </div>
          </Col>
          {/* Position/Progress */}
          <Col xs={12} md="auto" className="text-md-end">
            <div className="fw-semibold">{interview.companyName}</div>
            {interview.interviewerDisplayName && (
              <div className="text-muted" style={{ fontSize: 14 }}>Interviewer: {interview.interviewerDisplayName}</div>
            )}
            <div className="text-muted">{interview.jobRole}</div>
            <div className="text-muted">{interview.location}</div>
            <div className="d-flex align-items-center gap-2 justify-content-md-end mt-2 flex-wrap">
              <Badge bg={statusColor[interview.currentStatus] || "primary"}>
                {interview.currentStatus || "Scheduled"}
              </Badge>
              <span className="fw-semibold small">
                {roundsCompleted} of {totalRounds} rounds
              </span>
            </div>
            {/* Responsive ProgressBar */}
            <div className="mt-2 w-100 w-md-auto">
              <ProgressBar
                now={percent}
                style={{ height: 7, background: "#eaeaea" }}
              />
            </div>
            <div className="text-muted small mt-1">{percent}% Complete</div>
          </Col>
        </Row>
      </Card>

      <Nav
        variant="tabs"
        className="custom-tabs mb-3"
        activeKey={tab}
        onSelect={setTab}
      >
        <Nav.Item>
          <Nav.Link eventKey="timeline">Timeline</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="notes">Notes</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="activity">Activity Log</Nav.Link>
        </Nav.Item>
      </Nav>

      {/* CONTENT */}
      <div>
        {tab === "timeline" && (
          <RoundTimeline rounds={interview.rounds} onEdit={canEdit ? handleEditRound : undefined} />
        )}

        {tab === "notes" && (
          <Card body>
            <div>
              {interview.notes || <span className="text-muted">No notes</span>}
            </div>
          </Card>
        )}

        {tab === "activity" && (
          <Card body>
            {(!interview.changeLogs || interview.changeLogs.length === 0) ? (
              <div className="text-muted text-center py-4">No activity yet.</div>
            ) : (
              <div className="activity-log-list">
                {[...interview.changeLogs].reverse().map((log, idx) => {
                  const actionLabels = {
                    interview_created: 'Created interview',
                    interview_updated: 'Updated interview',
                    round_added: 'Added round',
                    round_updated: 'Updated round',
                    round_deleted: 'Deleted round',
                  };
                  const actionLabel = actionLabels[log.action] || log.action;
                  const ts = log.timestamp ? new Date(log.timestamp) : null;
                  return (
                    <div key={log._id || idx} className="d-flex gap-3 py-3 border-bottom" style={{ fontSize: 14 }}>
                      <div className="flex-shrink-0" style={{ width: 8, height: 8, borderRadius: '50%', background: log.action?.includes('delete') ? '#dc3545' : log.action?.includes('add') || log.action?.includes('create') ? '#198754' : '#0d6efd', marginTop: 6 }} />
                      <div className="flex-grow-1">
                        <div>
                          <strong>{actionLabel}</strong>
                          {log.roundName && <span className="text-muted"> — {log.roundName}</span>}
                        </div>
                        <div className="text-muted small mt-1">
                          by <strong>{log.performedBy}</strong>
                          {log.performedByRole && <span> ({log.performedByRole})</span>}
                          {ts && <span> · {ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Asia/Kolkata' })} at {ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span>}
                        </div>
                        {log.changes && log.changes.length > 0 && (
                          <div className="mt-1">
                            {log.changes.map((c, ci) => (
                              <div key={ci} className="small">
                                <span className="text-capitalize fw-medium">{c.field}</span>
                                {c.oldValue ? (
                                  <span>: <span className="text-decoration-line-through text-danger">{c.oldValue}</span> → <span className="text-success">{c.newValue}</span></span>
                                ) : (
                                  <span>: <span className="text-success">{c.newValue}</span></span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}
      </div>
      <AddEditRoundModal
        show={showRoundModal}
        onHide={() => setShowRoundModal(false)}
        onSave={handleSaveRound}
        mode={modalMode}
        initialData={editingRound}
        loading={savingRound}
        interviewId={id}
        studentId={interview.studentId?._id}
      />
    </div>
  );
};

export default InterviewDetailsPage;
